import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { AvailableBeneficiaryDto } from '../../community-action/dto/availableBeneficiary.dto';
import { EnrolledDto } from '../../community-action/dto/enrolled.dto';
import { FindSessionsLabelDto } from '../../community-action/dto/findSessionsLabel.dto';
import { SessionDto } from '../../community-action/dto/session.dto';
import { environment } from '../../environments/environment';
import { DisplayLabel } from '../models/displayLabel';
import { DisplaySessionDto } from '../models/displaySession';
import { Pagination } from '../pagination/pagination';
import { PaginationOptions } from '../pagination/pagination-options';
import { ApiService, FileResponse } from './api.service';
import { FileService } from './file.service';

type EnrollmentActionType = 'created' | 'updated' | 'deleted';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  constructor(
    private readonly api: ApiService,
    private readonly http: HttpClient,
    private readonly fileService: FileService,
  ) {}

  public sessionCreate: Subject<SessionDto> = new Subject<SessionDto>();
  public sessionCreated$: Observable<SessionDto> = this.sessionCreate.asObservable();

  private readonly enrollmentAction: Subject<{ action: EnrollmentActionType }> = new Subject<{ action: EnrollmentActionType }>();
  public get enrollmentAction$(): Observable<{ action: EnrollmentActionType }> {
    return this.enrollmentAction.asObservable();
  }

  public async findByActionId(actionId: number, options: PaginationOptions): Promise<Pagination<DisplaySessionDto>> {
    const uri: string = environment.api.communityAction + '/' + actionId + '/findSessionsByActionId/';
    return this.api.getPaginated<DisplaySessionDto>(options.buildUrl(uri));
  }

  public async findSessionById(sessionId: number): Promise<SessionDto> {
    const result: SessionDto = await this.api.get<SessionDto>(environment.api.communityAction + '/' + sessionId + '/sessionDetails');
    return result;
  }

  public async saveSession(session: SessionDto): Promise<void> {
    await this.api.post<void, SessionDto>(environment.api.communityAction + '/saveSession/', session);
    this.sessionCreate.next(session);
  }

  public async findSessionsLabel(dto: FindSessionsLabelDto): Promise<DisplayLabel[]> {
    return this.api.post<DisplayLabel[], FindSessionsLabelDto>(environment.api.communityAction + '/sessionsLabel', dto);
  }

  public async saveEnrolled(enrolled: EnrolledDto): Promise<void> {
    await this.api.post(environment.api.communityAction + '/saveEnroled/', enrolled);
    this.enrollmentAction.next({ action: 'created' });
    this.sessionCreate.next(undefined);
  }

  public async deleteStageAndBeneficiary(actionId: number, beneficiaryId: number): Promise<void> {
    await this.api.delete(`${environment.api.communityAction}/deleteEnrollment/${actionId}/${beneficiaryId}`);
    this.enrollmentAction.next({ action: 'deleted' });
    this.sessionCreate.next(undefined);
  }

  public findAvaillableBeneficiaryByText(text: string, searchAllBeneficiaries: boolean = false): Observable<AvailableBeneficiaryDto[]> {
    if (text && text !== '') {
      const url: string =
        environment.api.beneficiaries + '/find-availlable-beneficiary-by-text/' + '?q=' + text + '&searchAllBeneficiaries=' + searchAllBeneficiaries;
      return this.http.get<AvailableBeneficiaryDto[]>(url);
    }
    return of([]);
  }

  public async findEnrolledBeneficiaries(sessionId: number): Promise<EnrolledDto[]> {
    return this.api.get<EnrolledDto[]>(environment.api.communityAction + '/' + sessionId + '/findEnrolledBeneficiaries/');
  }

  public async updateEnrolled(updateEnrolled: EnrolledDto): Promise<void> {
    await this.api.post(environment.api.communityAction + '/saveEnroled/', updateEnrolled);
    this.enrollmentAction.next({ action: 'updated' });
    this.sessionCreate.next(undefined);
  }

  public async cancel(id: number): Promise<void> {
    await this.api.post(environment.api.communityAction + '/cancelSession/' + id, {});
    this.sessionCreate.next(undefined);
  }

  public async delete(id: number): Promise<void> {
    await this.api.delete(environment.api.communityAction + '/deleteSession/' + id);
    this.sessionCreate.next(undefined);
  }

  public async generateFile(sessionId: number, fileType: string, beneficiaryIds?: number[]): Promise<void> {
    const { filename, data }: FileResponse = await this.api.getFileWithName(
      environment.api.communityAction + '/' + sessionId + '/generate?type=' + fileType + '&benefs=' + beneficiaryIds.join('|'),
    );
    return this.fileService.saveAs(data, filename);
  }
}
